import React, { useEffect, useState } from "react";
import TradeContext from "./tradeContext";
import { defaultTrade } from "../../defaults/tradeDefault";
import PropTypes from "prop-types";
import { tradeBatchRetry, tradeSearch } from "../../api/trades.api";

const TradeProvider = ({ children }) => {
  const [trade, setTrade] = useState(defaultTrade);
  const [trades, setTrades] = useState([]);

  const [sortArray, setSortArray] = useState([]);
  const [searchParam, setSearchParam] = useState("trade_reference_number");
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(1);

  const [showToast, setShowToast] = useState(false);
  const [toastResult, setToastResult] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const [showTrade, setShowTrade] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [tradeFiles, setTradeFiles] = useState([]);

  const [tradeBatch, setTradeBatch] = useState([]);
  const [allBookedTradesChecked, setAllBookedTradesChecked] = useState(false);
  
  // State to control polling
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);

  useEffect(() => {
    getTrades();
  }, [searchParam, searchValue, page, rowsPerPage, JSON.stringify(sortArray)]);

  // Polling effect - runs every 15 seconds
  useEffect(() => {
    if (!isPollingEnabled) return;

    const intervalId = setInterval(() => {
      // Silently refresh trades in background
      getTrades();
    }, 15000); // 15 seconds

    // Cleanup interval on unmount or when polling is disabled
    return () => clearInterval(intervalId);
  }, [searchParam, searchValue, page, rowsPerPage, JSON.stringify(sortArray), isPollingEnabled]);

  const getTrades = async () => {
    try {
      const response = await tradeSearch(
        searchParam,
        searchValue,
        page,
        rowsPerPage,
        sortArray
      );

      if (response) {
        const newTrades = response.data.results;
        setTrades(newTrades);
        setPageCount(response.data.total_pages);
        setAllBookedTradesChecked(false);
        
        // Update the selected trade if it exists in the new results
        if (trade?.id) {
          const updatedSelectedTrade = newTrades.find(t => t.id === trade.id);
          if (updatedSelectedTrade) {
            setTrade(updatedSelectedTrade);
          }
        }
      } else {
        setTrades([]);
      }
    } catch (error) {
      setTrades([]);
    }
  };

  const retryTrades = async (batch) => {
    if (batch.length > 0) {
      const response = await tradeBatchRetry(batch);
      if (response.data.trades.length > 0) {
        const { trades } = response.data;
        setShowToast(true);
        setToastMessage(trades[0].message);
        setToastResult(trades[0].retry_success);
      } else {
        setShowToast(true);
        setToastMessage("Something went wrong!");
        setToastResult(false);
      }
    }
  };

  const updateTrade = (tradeId, updatedData) => {
    const trade = trades.find((t) => t.id === tradeId);
    
    if (!trade) {
      console.warn('Trade not found in trades array:', tradeId);
      // Still update the current trade if it matches
      if (trade && trade.id === tradeId) {
        setTrade((prevTrade) => ({ ...prevTrade, ...updatedData }));
      }
      return;
    }

    const newTrades = trades.map((t) =>
      t.id === tradeId ? { ...t, ...updatedData } : t
    );
    setTrades(newTrades);

    // If the currently selected trade is being updated, update it too
    if (trade && trade.id === tradeId) {
      setTrade((prevTrade) => ({ ...prevTrade, ...updatedData }));
    }
  };

  return (
    <TradeContext.Provider
      value={{
        trade,
        setTrade,
        trades,
        setTrades,
        getTrades,
        updateTrade,
        retryTrades,
        sortArray,
        setSortArray,
        searchParam,
        setSearchParam,
        searchValue,
        setSearchValue,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        pageCount,
        setPageCount,
        showToast,
        setShowToast,
        toastResult,
        setToastResult,
        toastMessage,
        setToastMessage,
        showTrade,
        setShowTrade,
        showNotes,
        setShowNotes,
        tradeBatch,
        setTradeBatch,
        showFiles,
        setShowFiles,
        tradeFiles,
        setTradeFiles,
        allBookedTradesChecked,
        setAllBookedTradesChecked,
        isPollingEnabled,
        setIsPollingEnabled,
      }}
    >
      {children}
    </TradeContext.Provider>
  );
};

TradeProvider.propTypes = {
  children: PropTypes.node,
};

export default TradeProvider;
